import React from 'react';
import { Button } from '../ui/button';
import { Check, Download, FileText, MapPin, Maximize2, Phone, Smile, Trash2, Ban, Video as VideoIcon, X } from 'lucide-react';
import { VoiceMessagePlayer } from './VoiceMessagePlayer';
import { Avatar, AvatarImage, AvatarFallback } from '../ui/avatar';
import { formatTimeOnly } from '../../utils/timezone';


const QUICK_REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🙏'];

const formatFileSize = (bytes) => {
  if (!bytes) return '';
  const sizes = ['Bytes', 'KB', 'MB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${Math.round((bytes / Math.pow(1024, i)) * 100) / 100} ${sizes[i]}`;
};

const groupReactions = (reactions = []) => {
  const grouped = {};
  reactions.forEach((reaction) => {
    if (!grouped[reaction.emoji]) grouped[reaction.emoji] = [];
    grouped[reaction.emoji].push(reaction);
  });
  return grouped;
};

const URL_REGEX = /((?:https?:\/\/|www\.)[^\s<>"']+)/gi;

const renderTextWithLinks = (text, isOwn, keyPrefix) => {
  const segments = text.split(URL_REGEX);
  return segments.map((seg, i) => {
    if (i % 2 === 1) {
      // Matched URL — peel trailing punctuation back into plain text
      const trailing = seg.match(/[.,;:!?)\]]+$/)?.[0] || '';
      const url = seg.slice(0, seg.length - trailing.length);
      const href = url.toLowerCase().startsWith('www.') ? `https://${url}` : url;
      return (
        <React.Fragment key={`${keyPrefix}-link-${i}`}>
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className={`underline underline-offset-2 break-all hover:opacity-80 ${
              isOwn ? 'text-white' : 'text-[#2563eb] dark:text-[#93c5fd]'
            }`}
            data-testid="chat-message-link"
          >
            {url}
          </a>
          {trailing}
        </React.Fragment>
      );
    }
    return seg;
  });
};

const renderMessageContent = (content, isOwn = false) => {
  if (!content) return null;
  const parts = content.split(/(@\w+(?:\s\w+)?)/g);

  return parts.map((part, index) => {
    if (part.startsWith('@')) {
      return (
        <span
          key={`${part}-${index}`}
          className={`px-1 rounded font-medium ${
            isOwn
              ? 'bg-white/20 text-white'
              : 'bg-[#2563eb]/10 text-[#2563eb] dark:bg-[#60a5fa]/20 dark:text-[#93c5fd]'
          }`}
        >
          {part}
        </span>
      );
    }
    return <React.Fragment key={`part-${index}`}>{renderTextWithLinks(part, isOwn, index)}</React.Fragment>;
  });
};

const isEmojiOnlyMessage = (content = '') => {
  const trimmed = content.trim();
  if (!trimmed) return false;
  const emojiParts = trimmed.match(/[\p{Extended_Pictographic}\p{Emoji_Presentation}\p{Regional_Indicator}\uFE0F\u200D]/gu) || [];
  if (emojiParts.length === 0) return false;
  const remainder = trimmed.replace(/[\p{Extended_Pictographic}\p{Emoji_Presentation}\p{Regional_Indicator}\uFE0F\u200D\s]/gu, '');
  return remainder.length === 0;
};

const emojiMessageClass = (content = '') => {
  const emojiCount = (content.trim().match(/[\p{Extended_Pictographic}\p{Emoji_Presentation}\p{Regional_Indicator}]/gu) || []).length;
  if (emojiCount <= 2) return 'text-5xl sm:text-6xl leading-none';
  if (emojiCount <= 5) return 'text-4xl sm:text-5xl leading-tight';
  return 'text-3xl sm:text-4xl leading-tight';
};

const getDateSeparatorLabel = (message, previousMessage) => {
  try {
    const ts = message.created_at;
    if (!ts) return null;
    const current = new Date(ts);
    const previous = previousMessage?.created_at ? new Date(previousMessage.created_at) : null;
    const isSameDay = previous
      && current.getFullYear() === previous.getFullYear()
      && current.getMonth() === previous.getMonth()
      && current.getDate() === previous.getDate();
    if (isSameDay) return null;

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const messageDay = new Date(current.getFullYear(), current.getMonth(), current.getDate());
    const diffDays = Math.round((today - messageDay) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays > 0 && diffDays < 7) return current.toLocaleDateString(undefined, { weekday: 'long' });
    return current.toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'short',
      year: current.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    });
  } catch {
    return null;
  }
};

const DateSeparator = React.memo(({ label, messageId }) => {
  if (!label) return null;
  return (
    <div className="flex items-center justify-center my-3" key={`sep-${messageId}`} data-testid={`chat-date-sep-${label}`}>
      <span className="text-[11px] uppercase tracking-wide font-semibold text-muted-foreground bg-muted/70 px-3 py-1 rounded-full">
        {label}
      </span>
    </div>
  );
});
DateSeparator.displayName = 'DateSeparator';

const ReadStatus = React.memo(({ status, messageId }) => {
  if (!status) return null;
  return (
    <span
      className={`ml-1 inline-flex items-center ${status.color}`}
      title={status.label}
      aria-label={status.label}
      data-testid={`chat-message-status-${messageId}`}
    >
      {status.ticks === 0 && <X className="w-3 h-3" />}
      {status.ticks === 1 && <Check className="w-3 h-3" />}
      {status.ticks === 2 && (
        <span className="inline-flex -space-x-1.5">
          <Check className="w-3 h-3" />
          <Check className="w-3 h-3" />
        </span>
      )}
    </span>
  );
});
ReadStatus.displayName = 'ReadStatus';

const ReactionControls = React.memo(({ message, isOwn, userId, openReactionMenuId, setOpenReactionMenuId, onToggleReaction }) => (
  <>
    <div className={`absolute top-0 ${isOwn ? 'left-0 -translate-x-full pr-2' : 'right-0 translate-x-full pl-2'} opacity-0 group-hover:opacity-100 transition-opacity`}>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-7 w-7 p-0"
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setOpenReactionMenuId(openReactionMenuId === message.id ? null : message.id);
        }}
        data-testid={`chat-reaction-menu-button-${message.id}`}
      >
        <Smile className="w-4 h-4 text-muted-foreground" />
      </Button>
    </div>

    {openReactionMenuId === message.id && (
      <div
        className={`absolute top-8 ${isOwn ? 'right-0' : 'left-0'} z-[9999] flex gap-1 rounded-md border bg-popover p-2 text-popover-foreground shadow-lg`}
        data-testid={`chat-reaction-menu-${message.id}`}
      >
        {QUICK_REACTIONS.map((emoji) => (
          <button
            key={emoji}
            type="button"
            onClick={() => {
              onToggleReaction(message.id, emoji);
              setOpenReactionMenuId(null);
            }}
            className="text-2xl hover:bg-muted p-1.5 rounded transition-colors"
            data-testid={`chat-reaction-option-${message.id}-${emoji.codePointAt(0).toString(16)}`}
          >
            {emoji}
          </button>
        ))}
      </div>
    )}

    {message.reactions && message.reactions.length > 0 && (
      <div className={`flex flex-wrap gap-1 mt-1 ${isOwn ? 'justify-end' : 'justify-start'}`}>
        {Object.entries(groupReactions(message.reactions)).map(([emoji, reactors]) => (
          <button
            key={emoji}
            type="button"
            onClick={() => onToggleReaction(message.id, emoji)}
            className={`text-sm px-2 py-0.5 rounded-full flex items-center gap-1 transition-colors ${
              reactors.some((reactor) => reactor.user_id === userId)
                ? 'bg-[#2563eb]/20 border border-[#2563eb]/40'
                : 'bg-muted hover:bg-muted/80'
            }`}
            title={reactors.map((reactor) => reactor.user_name).join(', ')}
            data-testid={`chat-reaction-pill-${message.id}-${emoji.codePointAt(0).toString(16)}`}
          >
            <span className="text-lg">{emoji}</span>
            <span className="text-muted-foreground">{reactors.length}</span>
          </button>
        ))}
      </div>
    )}
  </>
));
ReactionControls.displayName = 'ReactionControls';

const LinkPreviewCard = React.memo(({ preview, isOwn }) => (
  <a
    href={preview.url}
    target="_blank"
    rel="noopener noreferrer"
    onClick={(e) => e.stopPropagation()}
    className={`mt-2 flex gap-2.5 rounded-md border p-2 no-underline transition-opacity hover:opacity-90 ${
      isOwn ? 'bg-white/10 border-white/20' : 'bg-background/70 border-border'
    }`}
    data-testid="chat-link-preview"
  >
    {preview.image && (
      <img
        src={preview.image}
        alt=""
        loading="lazy"
        decoding="async"
        className="h-16 w-16 rounded object-cover shrink-0 bg-black/10"
        onError={(e) => { e.currentTarget.style.display = 'none'; }}
      />
    )}
    <span className="min-w-0 flex flex-col gap-0.5">
      <span className={`text-xs font-semibold leading-snug line-clamp-2 ${isOwn ? 'text-white' : 'text-foreground'}`}>
        {preview.title || preview.domain}
      </span>
      {preview.description && (
        <span className={`text-xs leading-snug line-clamp-2 ${isOwn ? 'text-white/70' : 'text-muted-foreground'}`}>
          {preview.description}
        </span>
      )}
      <span className={`text-[11px] truncate ${isOwn ? 'text-white/60' : 'text-muted-foreground/80'}`}>
        {preview.site_name || preview.domain}
      </span>
    </span>
  </a>
));
LinkPreviewCard.displayName = 'LinkPreviewCard';

const TextMessage = React.memo(({ message, isOwn, userId, openReactionMenuId, setOpenReactionMenuId, onToggleReaction }) => {
  const emojiOnly = isEmojiOnlyMessage(message.content);
  return (
    <div className="group relative max-w-full min-w-0 overflow-visible">
      <div
        className={`max-w-full overflow-hidden rounded-lg ${
          emojiOnly
            ? 'bg-transparent px-1 py-1'
            : `px-4 py-2 ${isOwn ? 'bg-[#2563eb] text-white' : 'bg-slate-200 dark:bg-slate-700 text-foreground'}`
        }`}
      >
        <p
          className={`${emojiOnly ? `${emojiMessageClass(message.content)} font-emoji drop-shadow-sm` : 'text-sm'} whitespace-pre-wrap break-words [overflow-wrap:anywhere]`}
          style={emojiOnly ? { fontFamily: 'Apple Color Emoji, Segoe UI Emoji, Noto Color Emoji, emoji' } : undefined}
          data-testid={emojiOnly ? `chat-emoji-message-${message.id}` : undefined}
        >
          {emojiOnly ? message.content : renderMessageContent(message.content, isOwn)}
        </p>
        {!emojiOnly && message.link_preview && <LinkPreviewCard preview={message.link_preview} isOwn={isOwn} />}
      </div>
      <ReactionControls
        message={message}
        isOwn={isOwn}
        userId={userId}
        openReactionMenuId={openReactionMenuId}
        setOpenReactionMenuId={setOpenReactionMenuId}
        onToggleReaction={onToggleReaction}
      />
    </div>
  );
});
TextMessage.displayName = 'TextMessage';

const ChatImage = React.memo(({ message, onOpenLightbox }) => {
  const [loaded, setLoaded] = React.useState(false);
  const [failed, setFailed] = React.useState(false);
  // Use the real image ratio when known (clamped so extreme panoramas/portraits
  // stay usable in the thread); fall back to 4:3 for legacy messages.
  const ratio = message.image_width > 0 && message.image_height > 0
    ? Math.min(Math.max(message.image_width / message.image_height, 0.6), 1.9)
    : 4 / 3;
  return (
    <button
      type="button"
      onClick={() => onOpenLightbox({ id: message.id, url: message.file_url, name: message.file_name, type: 'image' })}
      className="relative block w-60 sm:w-72 max-w-full overflow-hidden rounded-lg bg-black/10 dark:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/40"
      style={{ aspectRatio: String(ratio) }}
      data-testid={`chat-image-open-${message.id}`}
      aria-label={`Open ${message.file_name} full screen`}
    >
      {!loaded && !failed && (
        <span
          className="absolute inset-0 animate-pulse bg-slate-300/50 dark:bg-slate-600/50"
          data-testid={`chat-image-placeholder-${message.id}`}
          aria-hidden="true"
        />
      )}
      <img
        src={message.file_url}
        alt={message.file_name}
        loading="lazy"
        decoding="async"
        onLoad={() => setLoaded(true)}
        onError={() => setFailed(true)}
        className={`h-full w-full object-cover cursor-zoom-in transition-opacity duration-300 ${loaded ? 'opacity-100' : 'opacity-0'}`}
      />
      {failed && (
        <span className="absolute inset-0 flex items-center justify-center text-xs opacity-70">Image unavailable</span>
      )}
    </button>
  );
});
ChatImage.displayName = 'ChatImage';

const FileMessage = React.memo(({ message, isOwn, onOpenLightbox, onDownload }) => {
  const handleDownload = (event) => {
    event.preventDefault();
    event.stopPropagation();
    onDownload?.(message);
  };
  return (
  <div className={`rounded-lg p-3 ${isOwn ? 'bg-[#2563eb] text-white' : 'bg-slate-200 dark:bg-slate-700 text-foreground'}`}>
    {message.file_type?.startsWith('image/') ? (
      <div className="space-y-2">
        <ChatImage message={message} onOpenLightbox={onOpenLightbox} />
        <div className="flex items-center justify-between">
          <p className="text-xs truncate min-w-0 mr-2">{message.file_name}</p>
          <a href={message.file_url} download={message.file_name} rel="noopener noreferrer" className="opacity-70 hover:opacity-100 shrink-0" onClick={handleDownload} data-testid={`chat-file-download-${message.id}`} aria-label={`Download ${message.file_name}`}>
            <Download className="w-4 h-4" />
          </a>
        </div>
      </div>
    ) : message.file_type?.startsWith('video/') ? (
      <div className="space-y-2">
        <div className="relative w-60 sm:w-72 max-w-full aspect-video overflow-hidden rounded-lg bg-black">
          <video src={message.file_url} controls playsInline preload="metadata" className="h-full w-full object-contain" data-testid={`chat-video-${message.id}`}>
            Your browser does not support video playback.
          </video>
          <button
            type="button"
            onClick={() => onOpenLightbox({ id: message.id, url: message.file_url, name: message.file_name, type: 'video' })}
            className="absolute top-2 right-2 inline-flex items-center justify-center h-8 w-8 rounded-full bg-black/60 text-white hover:bg-black/80 transition-colors"
            data-testid={`chat-video-open-${message.id}`}
            aria-label={`Open ${message.file_name} full screen`}
            title="Open full screen"
          >
            <Maximize2 className="w-4 h-4" />
          </button>
        </div>
        <div className="flex items-center justify-between">
          <p className="text-xs truncate min-w-0 mr-2">{message.file_name}</p>
          <a href={message.file_url} download={message.file_name} rel="noopener noreferrer" className="opacity-70 hover:opacity-100 shrink-0" onClick={handleDownload} data-testid={`chat-file-download-${message.id}`} aria-label={`Download ${message.file_name}`}>
            <Download className="w-4 h-4" />
          </a>
        </div>
      </div>
    ) : message.file_type === 'application/pdf' ? (
      <div className="space-y-2">
        <button
          type="button"
          onClick={() => onOpenLightbox({ id: message.id, url: message.file_url, name: message.file_name, type: 'pdf' })}
          className="relative block w-full h-44 overflow-hidden rounded-lg bg-white border border-slate-300 focus:outline-none focus:ring-2 focus:ring-white/40"
          data-testid={`chat-pdf-open-${message.id}`}
          aria-label={`Open ${message.file_name} full screen`}
        >
          <iframe src={`${message.file_url}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`} title={message.file_name} className="pointer-events-none w-full h-full bg-white" loading="lazy" />
          <span className="absolute inset-0 cursor-zoom-in" aria-hidden="true" />
          <span className="absolute top-2 right-2 inline-flex items-center gap-1 rounded-full bg-black/60 text-white text-[10px] font-semibold px-2 py-0.5">
            <FileText className="w-3 h-3" /> PDF
          </span>
        </button>
        <div className="flex items-center justify-between">
          <p className="text-xs truncate min-w-0 mr-2">{message.file_name}</p>
          <a href={message.file_url} download={message.file_name} rel="noopener noreferrer" className="opacity-70 hover:opacity-100 shrink-0" onClick={handleDownload} data-testid={`chat-file-download-${message.id}`} aria-label={`Download ${message.file_name}`}>
            <Download className="w-4 h-4" />
          </a>
        </div>
      </div>
    ) : (
      <div className="flex items-center gap-2">
        <FileText className="w-5 h-5" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">{message.file_name}</p>
          <p className="text-xs opacity-70">{formatFileSize(message.file_size)}</p>
        </div>
        <a href={message.file_url} download={message.file_name} rel="noopener noreferrer" onClick={handleDownload} data-testid={`chat-file-download-${message.id}`} aria-label={`Download ${message.file_name}`}>
          <Download className="w-4 h-4" />
        </a>
      </div>
    )}
  </div>
  );
});
FileMessage.displayName = 'FileMessage';

const MessageRow = React.memo(({ message, previousMessage, userId, isAdmin, canModerate, firstUnreadMessageId, openReactionMenuId, setOpenReactionMenuId, onToggleReaction, onOpenLightbox, onDeleteMessage, onDownload, showSenderAvatars, senderAvatarUrl }) => {
  const isOwn = message.sender_id === userId;
  const isSystem = message.type === 'system' || message.sender_id === 'system';
  const dateLabel = getDateSeparatorLabel(message, previousMessage);

  if (isSystem) {
    return (
      <React.Fragment>
        <DateSeparator label={dateLabel} messageId={message.id} />
        <div className="flex justify-center">
          <div className="bg-muted/50 text-muted-foreground text-xs px-3 py-1 rounded-full">
            {message.content}
          </div>
        </div>
      </React.Fragment>
    );
  }

  let readStatus = null;
  if (isOwn) {
    if (message._pending) {
      readStatus = { ticks: 1, color: 'text-white/60', label: 'Sending' };
    } else if (message._failed) {
      readStatus = { ticks: 0, color: 'text-red-300', label: 'Failed' };
    } else {
      const otherReaders = (message.read_by || []).filter((uid) => uid && uid !== userId);
      if (otherReaders.length > 0) {
        readStatus = { ticks: 2, color: 'text-sky-300', label: 'Seen' };
      } else {
        const otherDelivered = (message.delivered_to || []).filter((uid) => uid && uid !== userId);
        readStatus = otherDelivered.length > 0
          ? { ticks: 2, color: 'text-white/70', label: 'Delivered' }
          : { ticks: 1, color: 'text-white/70', label: 'Sent' };
      }
    }
  }

  let timestamp = message.created_at;
  if (timestamp && !timestamp.endsWith('Z') && !timestamp.includes('+')) timestamp += 'Z';

  return (
    <React.Fragment>
      <DateSeparator label={dateLabel} messageId={message.id} />
      {firstUnreadMessageId === message.id && (
        <div className="flex items-center gap-3 my-3" data-testid="chat-unread-divider">
          <div className="h-px flex-1 bg-[#2563eb]/30" />
          <span className="rounded-full bg-[#2563eb]/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-[#2563eb]">
            Unread messages
          </span>
          <div className="h-px flex-1 bg-[#2563eb]/30" />
        </div>
      )}
      <div
        data-message-id={message.id}
        data-testid={`chat-message-row-${message.id}`}
        className={`group flex ${isOwn ? 'justify-end' : 'justify-start'} min-w-0 ${message._failed ? 'opacity-70' : ''} ${message._pending ? 'opacity-80' : ''}`}
      >
        {!isOwn && showSenderAvatars && (
          <Avatar className="h-8 w-8 shrink-0 mr-2 mt-4" data-testid={`chat-message-sender-avatar-${message.id}`}>
            {senderAvatarUrl && <AvatarImage src={senderAvatarUrl} className="object-cover" />}
            <AvatarFallback className="bg-[#2563eb] text-white text-xs font-semibold">
              {message.sender_name?.[0]?.toUpperCase() || 'U'}
            </AvatarFallback>
          </Avatar>
        )}
        <div className={`max-w-[80%] sm:max-w-[70%] min-w-0 overflow-visible ${isOwn ? 'items-end' : 'items-start'} flex flex-col gap-1`}>
          {!isOwn && <span className="text-xs text-muted-foreground px-3">{message.sender_name}</span>}

          {message.deleted ? (
            <div
              className={`rounded-lg px-4 py-2 ${isOwn ? 'bg-[#2563eb]/40 text-white' : 'bg-slate-200/60 dark:bg-slate-700/60 text-muted-foreground'}`}
              data-testid={`chat-message-deleted-${message.id}`}
            >
              <p className="text-sm italic flex items-center gap-1.5 opacity-80">
                <Ban className="w-3.5 h-3.5 shrink-0" /> This message was deleted
              </p>
            </div>
          ) : (
            <>
              {message.type === 'text' && (
                <TextMessage
                  message={message}
                  isOwn={isOwn}
                  userId={userId}
                  openReactionMenuId={openReactionMenuId}
                  setOpenReactionMenuId={setOpenReactionMenuId}
                  onToggleReaction={onToggleReaction}
                />
              )}

              {message.type === 'file' && <FileMessage message={message} isOwn={isOwn} onOpenLightbox={onOpenLightbox} onDownload={onDownload} />}

              {message.type === 'voice' && (
                <div className={`rounded-lg px-3 py-2.5 w-full sm:min-w-64 max-w-full ${isOwn ? 'bg-[#2563eb] text-white' : 'bg-slate-200 dark:bg-slate-700 text-foreground'}`}>
                  <VoiceMessagePlayer src={message.file_url} duration={message.duration} isOwn={isOwn} testId={`voice-player-${message.id}`} />
                </div>
              )}

              {message.type === 'call' && (
                <div
                  className={`rounded-lg px-4 py-2 flex items-center gap-2 ${isOwn ? 'bg-[#2563eb]/70 text-white' : 'bg-slate-200 dark:bg-slate-700 text-foreground'}`}
                  data-testid={`chat-call-log-${message.id}`}
                >
                  {message.call_media === 'video' ? (
                    <VideoIcon className={`w-4 h-4 shrink-0 ${message.call_outcome === 'completed' ? 'text-green-400' : 'text-red-400'}`} />
                  ) : (
                    <Phone className={`w-4 h-4 shrink-0 ${message.call_outcome === 'completed' ? 'text-green-400' : 'text-red-400'}`} />
                  )}
                  <span className="text-sm">{message.content}</span>
                </div>
              )}

              {message.type === 'location' && message.location && (
                <div className={`rounded-lg p-3 ${isOwn ? 'bg-[#2563eb] text-white' : 'bg-slate-200 dark:bg-slate-700 text-foreground'}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <MapPin className="w-4 h-4" />
                    <span className="text-sm font-medium">{message.location_name || 'Location'}</span>
                  </div>
                  <a
                    href={`https://www.google.com/maps?q=${message.location.lat},${message.location.lng}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs underline"
                    data-testid={`chat-location-link-${message.id}`}
                  >
                    {message.location.lat.toFixed(5)}, {message.location.lng.toFixed(5)}
                  </a>
                </div>
              )}
            </>
          )}

          <span className="text-xs text-muted-foreground px-3 flex items-center gap-1">
            {timestamp ? formatTimeOnly(timestamp) : ''}
            <ReadStatus status={readStatus} messageId={message.id} />
            {!message.deleted && !message._pending && !message._failed && (isOwn || isAdmin || canModerate) && (
              <button
                type="button"
                onClick={() => onDeleteMessage(message)}
                className="ml-1 rounded p-0.5 text-muted-foreground/70 hover:text-red-500 transition-colors opacity-60 sm:opacity-0 sm:group-hover:opacity-100 focus:opacity-100"
                title="Delete message"
                aria-label="Delete message"
                data-testid={`chat-message-delete-${message.id}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </span>
        </div>
      </div>
    </React.Fragment>
  );
});
MessageRow.displayName = 'MessageRow';

const TypingIndicator = React.memo(({ typingUsers = [] }) => {
  if (typingUsers.length === 0) return null;
  let label = `${typingUsers.length} people are typing…`;
  if (typingUsers.length === 1) label = `${typingUsers[0].user_name} is typing…`;
  if (typingUsers.length === 2) label = `${typingUsers[0].user_name} and ${typingUsers[1].user_name} are typing…`;

  return (
    <div className="flex justify-start" data-testid="chat-typing-indicator">
      <div className="rounded-2xl px-3 py-2 bg-slate-200 dark:bg-slate-700 text-foreground inline-flex items-center gap-2">
        <span className="flex gap-1">
          <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground animate-bounce" style={{ animationDelay: '0ms' }} />
          <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground animate-bounce" style={{ animationDelay: '120ms' }} />
          <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground animate-bounce" style={{ animationDelay: '240ms' }} />
        </span>
        <span className="text-xs text-muted-foreground">{label}</span>
      </div>
    </div>
  );
});
TypingIndicator.displayName = 'TypingIndicator';

export const ChatMessageList = React.memo(({ messages, userId, isAdmin, canModerate, firstUnreadMessageId, typingUsers, loadingOlder, openReactionMenuId, setOpenReactionMenuId, onToggleReaction, onOpenLightbox, onDeleteMessage, onDownload, messagesEndRef, contentRef, showSenderAvatars = false, avatarUrls = {} }) => (
  <div ref={contentRef} className="space-y-4 min-w-0">
    {loadingOlder && (
      <div className="flex justify-center py-1" data-testid="chat-loading-older">
        <span className="inline-flex items-center gap-2 text-xs text-muted-foreground bg-muted/70 px-3 py-1 rounded-full">
          <span className="w-3 h-3 rounded-full border-2 border-muted-foreground/30 border-t-muted-foreground animate-spin" aria-hidden="true" />
          Loading older messages…
        </span>
      </div>
    )}
    {messages.map((message, index) => (
      <MessageRow
        key={message.id}
        message={message}
        previousMessage={index > 0 ? messages[index - 1] : null}
        userId={userId}
        isAdmin={isAdmin}
        canModerate={canModerate}
        firstUnreadMessageId={firstUnreadMessageId}
        openReactionMenuId={openReactionMenuId}
        setOpenReactionMenuId={setOpenReactionMenuId}
        onToggleReaction={onToggleReaction}
        onOpenLightbox={onOpenLightbox}
        onDeleteMessage={onDeleteMessage}
        onDownload={onDownload}
        showSenderAvatars={showSenderAvatars}
        senderAvatarUrl={avatarUrls[message.sender_id] || null}
      />
    ))}
    <TypingIndicator typingUsers={typingUsers} />
    <div ref={messagesEndRef} />
  </div>
));
ChatMessageList.displayName = 'ChatMessageList';